
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, MapPin, Phone, Globe, Plus, Check, Loader2, Image as ImageIcon, Download, Navigation } from 'lucide-react';
import { PlaceResult } from '@/types/place';
import { Lead } from '@/types/lead';
import { getPhotoUrl } from '@/services/placesService';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { calculateDistance, formatDistance } from '@/utils/distanceUtils';

interface PlaceSearchProps {
  results: PlaceResult[];
  isLoading: boolean;
  onImport: (place: PlaceResult, searchQuery?: string) => Promise<Lead | null>;
  searchQuery?: string;
  onRetry?: () => void;
  searchLocation?: {
    lat: number;
    lng: number;
  };
  useRadius?: boolean;
}

const PlaceSearch: React.FC<PlaceSearchProps> = ({ 
  results, 
  isLoading, 
  onImport,
  searchQuery,
  onRetry,
  searchLocation,
  useRadius
}) => {
  const { toast } = useToast();
  const [importing, setImporting] = useState<Record<string, boolean>>({});
  const [importedLeads, setImportedLeads] = useState<Record<string, string>>({});
  const [placeImages, setPlaceImages] = useState<Record<string, string>>({});
  const [loadingImages, setLoadingImages] = useState<Record<string, boolean>>({});
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedPlaces, setSelectedPlaces] = useState<Set<string>>(new Set());
  const [bulkProgress, setBulkProgress] = useState<{current: number, total: number} | null>(null);
  const [isAuthed, setIsAuthed] = useState(false);

  // Check auth status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthed(!!session);
    };
    
    checkAuth();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthed(!!session);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    console.log('==== PLACE SEARCH COMPONENT ====');
    console.log(`Rendering ${results.length} place results`);
    results.forEach((place, index) => {
      console.log(`Result ${index + 1}: ${place.name} (place_id: ${place.place_id})`);
    });
    
    const fetchImages = async () => {
      for (const place of results) {
        if (place.photos && place.photos.length > 0 && !placeImages[place.place_id]) {
          try {
            setLoadingImages(prev => ({ ...prev, [place.place_id]: true }));
            const photoUrl = await getPhotoUrl(place.photos[0].photo_reference);
            setPlaceImages(prev => ({ ...prev, [place.place_id]: photoUrl }));
          } catch (error) {
            console.error(`Error fetching image for ${place.name}:`, error);
          } finally {
            setLoadingImages(prev => ({ ...prev, [place.place_id]: false }));
          }
        }
      }
    };

    fetchImages();
  }, [results]);

  const [importErrors, setImportErrors] = useState<Record<string, { message: string; retryable: boolean }>>({});

  const handleImport = async (place: PlaceResult) => {
    console.log(`[${new Date().toISOString()}] Import initiated for: ${place.name}`);
    
    setImporting(prev => ({ ...prev, [place.place_id]: true }));
    setImportErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[place.place_id];
      return newErrors;
    });
    
    try {
      const importedLead = await onImport(place, searchQuery);
      
      // Store the lead ID for navigation
      if (importedLead?.id) {
        setImportedLeads(prev => ({ ...prev, [place.place_id]: importedLead.id }));
        
      }
    } catch (error) {
      console.error(`[${new Date().toISOString()}] Import failed for ${place.name}:`, error);
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const isRetryable = !errorMessage.includes('quota') && 
                         !errorMessage.includes('auth') && 
                         !errorMessage.includes('company');
      
      setImportErrors(prev => ({
        ...prev,
        [place.place_id]: {
          message: errorMessage,
          retryable: isRetryable
        }
      }));
      
      // Error toast is shown by importService
    } finally {
      setImporting(prev => ({ ...prev, [place.place_id]: false }));
    }
  };

  const handleBulkImport = async () => {
    const placesToImport = results.filter(p => selectedPlaces.has(p.place_id) && !importedLeads[p.place_id]);
    
    if (placesToImport.length === 0) {
      toast({
        title: "No leads to import",
        description: "All selected leads have already been imported.",
      });
      return;
    }
    
    setImporting(prev => {
      const newState = { ...prev };
      placesToImport.forEach(p => newState[p.place_id] = true);
      return newState;
    });
    
    setBulkProgress({ current: 0, total: placesToImport.length });
    
    const imported: string[] = [];
    const failed: string[] = [];
    
    for (let i = 0; i < placesToImport.length; i++) {
      const place = placesToImport[i];
      try {
        const lead = await onImport(place, searchQuery);
        if (lead?.id) {
          imported.push(place.name);
          setImportedLeads(prev => ({ ...prev, [place.place_id]: lead.id }));
        }
      } catch (error) {
        console.error(`Failed to import ${place.name}:`, error);
        failed.push(place.name);
      } finally {
        setImporting(prev => ({ ...prev, [place.place_id]: false }));
        setBulkProgress({ current: i + 1, total: placesToImport.length });
      }
    }
    
    setBulkProgress(null);
    
    toast({
      title: "Bulk Import Complete",
      description: `Successfully imported ${imported.length} lead(s).${failed.length > 0 ? ` ${failed.length} failed.` : ''}`,
    });
    
    setSelectionMode(false);
    setSelectedPlaces(new Set());
  };

  if (isLoading && results.length === 0) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </CardHeader>
            <CardContent>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </CardContent>
            <CardFooter>
              <div className="h-10 bg-gray-200 rounded w-full"></div>
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="mb-4 text-muted-foreground">
            <svg className="h-16 w-16 mx-auto opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 10l4 4m0-4l-4 4" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold mb-2">No Results Found</h3>
          <p className="text-muted-foreground mb-4">
            We couldn't find any businesses matching your search criteria.
          </p>
          <div className="bg-muted/30 rounded-lg p-4 text-left">
            <p className="text-sm font-medium mb-2">Try these suggestions:</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>Check your spelling</li>
              <li>Use more general search terms</li>
              <li>Try a different location or increase the search radius</li>
              <li>Remove the radius restriction for broader results</li>
            </ul>
          </div>
          {typeof onRetry === 'function' && (
            <div className="mt-4">
              <Button variant="outline" size="sm" onClick={onRetry}>
                Try Again
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {bulkProgress && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="w-[90vw] max-w-md p-6">
            <CardHeader>
              <CardTitle>Importing Leads...</CardTitle>
              <CardDescription>
                {bulkProgress.current} of {bulkProgress.total} completed
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Progress value={(bulkProgress.current / bulkProgress.total) * 100} />
            </CardContent>
          </Card>
        </div>
      )}
      
      {results.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <Checkbox 
              checked={selectedPlaces.size === results.length && results.length > 0}
              onCheckedChange={(checked) => {
                if (checked) {
                  setSelectedPlaces(new Set(results.map(p => p.place_id)));
                } else {
                  setSelectedPlaces(new Set());
                }
              }}
            />
            <span className="text-sm font-medium">
              {selectedPlaces.size > 0 
                ? `${selectedPlaces.size} selected` 
                : 'Select all'}
            </span>
          </div>
          
          <div className="flex gap-2">
            {selectedPlaces.size > 0 && (
              <Button 
                size="sm" 
                onClick={handleBulkImport}
                disabled={importing[results[0]?.place_id] || bulkProgress !== null}
              >
                <Download className="h-4 w-4 mr-1" />
                Import {selectedPlaces.size}
              </Button>
            )}
            
            <Button 
              size="sm" 
              variant="outline"
              onClick={() => {
                setSelectionMode(!selectionMode);
                setSelectedPlaces(new Set());
              }}
            >
              {selectionMode ? 'Cancel' : 'Select Multiple'}
            </Button>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-4">
        {results.map(place => (
          <Card 
            key={place.place_id} 
            className={cn(
              "flex flex-col h-full w-full overflow-hidden relative",
              selectionMode && "cursor-pointer",
              selectedPlaces.has(place.place_id) && "ring-2 ring-primary"
            )}
            onClick={() => {
              if (selectionMode) {
                const newSelected = new Set(selectedPlaces);
                if (newSelected.has(place.place_id)) {
                  newSelected.delete(place.place_id);
                } else {
                  newSelected.add(place.place_id);
                }
                setSelectedPlaces(newSelected);
              }
            }}
          >
            <div className="hidden">Debug place_id: {place.place_id}</div>
            
            {selectionMode && (
              <div className="absolute top-2 right-2 z-10">
                <Checkbox 
                  checked={selectedPlaces.has(place.place_id)}
                  className="bg-background shadow-md"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            
            <div className="w-full h-32 sm:h-40 relative bg-muted overflow-hidden">
            {loadingImages[place.place_id] ? (
              <Skeleton className="h-full w-full" />
            ) : placeImages[place.place_id] ? (
              <img 
                src={placeImages[place.place_id]} 
                alt={place.name} 
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex items-center justify-center h-full w-full bg-muted">
                <ImageIcon className="h-10 w-10 text-muted-foreground opacity-50" />
              </div>
            )}
          </div>
          
            <CardHeader className="pb-2 space-y-1 flex-shrink-0">
              <CardTitle className="text-sm sm:text-base font-semibold line-clamp-1 overflow-hidden text-ellipsis">
                {place.name}
              </CardTitle>
              <CardDescription className="flex flex-col gap-1 text-xs w-full">
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3 flex-shrink-0" /> 
                  <span className="line-clamp-1 overflow-hidden text-ellipsis w-full">
                    {place.vicinity || place.formatted_address}
                  </span>
                </div>
                {useRadius && searchLocation && place.geometry?.location && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Navigation className="h-3 w-3 flex-shrink-0" />
                    <span>
                      {formatDistance(
                        calculateDistance(
                          searchLocation.lat,
                          searchLocation.lng,
                          place.geometry.location.lat,
                          place.geometry.location.lng
                        )
                      )} away
                    </span>
                  </div>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="pb-2 flex-grow overflow-hidden">
              {place.types && place.types.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2 max-w-full">
                  {place.types
                    .filter(type => !['establishment', 'point_of_interest', 'political', 'premise'].includes(type))
                    .slice(0, 3)
                    .map(type => (
                      <Badge key={type} variant="outline" className="capitalize text-xs px-1.5 py-0.5 whitespace-nowrap text-ellipsis overflow-hidden max-w-[100px]">
                        {type.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                </div>
              )}
              
              {place.rating && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Star className="h-3 w-3 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                  <span>{place.rating}</span>
                  {place.user_ratings_total && (
                    <span className="text-xs">({place.user_ratings_total})</span>
                  )}
                </div>
              )}
            </CardContent>
            <CardFooter className="pt-2 pb-3 sm:pb-4 flex-shrink-0 flex-col gap-2">
              {importedLeads[place.place_id] ? (
                <Button 
                  variant="outline" 
                  className="w-full h-9 text-xs sm:text-sm text-green-600 border-green-600 hover:bg-green-50 dark:hover:bg-green-950/40"
                  size="sm"
                  asChild
                >
                  <Link to={`/leads/${importedLeads[place.place_id]}`}>
                    <Check className="h-3 w-3 mr-1" />
                    View Lead
                  </Link>
                </Button>
              ) : importErrors[place.place_id] ? (
                <div className="w-full space-y-2">
                  <div className="text-xs text-destructive text-center">
                    Import failed
                  </div>
                  {importErrors[place.place_id].retryable && (
                    <Button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImport(place);
                      }}
                      disabled={importing[place.place_id]}
                      variant="outline"
                      className="w-full h-9 text-xs sm:text-sm"
                      size="sm"
                    >
                      {importing[place.place_id] ? (
                        <>
                          <Loader2 className="animate-spin h-3 w-3 mr-1" />
                          Retrying...
                        </>
                      ) : (
                        <>
                          <Plus className="h-3 w-3 mr-1" />
                          Retry Import
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ) : (
                <Button 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isAuthed) {
                      toast({
                        variant: "destructive",
                        title: "Sign in required",
                        description: "Please sign in to import leads.",
                      });
                      return;
                    }
                    handleImport(place);
                  }}
                  disabled={!isAuthed || importing[place.place_id]}
                  className="w-full h-9 text-xs sm:text-sm"
                  size="sm"
                >
                  {importing[place.place_id] ? (
                    <>
                      <Loader2 className="animate-spin h-3 w-3 mr-1" />
                      Importing...
                    </>
                  ) : !isAuthed ? (
                    <>
                      <Plus className="h-3 w-3 mr-1" />
                      Sign in to Import
                    </>
                  ) : (
                    <>
                      <Plus className="h-3 w-3 mr-1" />
                      Import as Lead
                    </>
                  )}
                </Button>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>
    </>
  );
};

export default PlaceSearch;
